// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import type { AdminUser } from '../../types/admin';
import { format } from 'date-fns';
import { AdminCopyButton } from './AdminCopyButton';
import { useAdminToast } from './useAdminToast';

interface AdminUserDetailModalProps {
  isOpen: boolean;
  user: AdminUser | null;
  onClose: () => void;
  onUserUpdate?: () => void;
}

interface UserActivity {
  id: string;
  type: 'subscription' | 'notification' | 'profile_update';
  description: string;
  timestamp: string;
  metadata?: any;
}

export const AdminUserDetailModal: React.FC<AdminUserDetailModalProps> = ({
  isOpen,
  user,
  onClose,
  onUserUpdate,
}) => {
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [userStats, setUserStats] = useState<{
    subscriptions: number;
    notifications: number;
    lastActivity: string | null;
  }>({ subscriptions: 0, notifications: 0, lastActivity: null });
  const { showSuccess, showError } = useAdminToast();

  useEffect(() => {
    if (isOpen && user) {
      loadUserData();
    }
  }, [isOpen, user]);

  const loadUserData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Load subscriptions
      const { data: subscriptions } = await supabase
        .from('user_topic_subscriptions')
        .select('id, created_at, law_topics:topic_id (name)')
        .eq('user_id', user.id);

      // Load notifications
      const { data: notifications } = await supabase
        .from('notifications')
        .select('id, created_at, title, email_sent')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      // Build activity timeline
      const activityList: UserActivity[] = [];

      // Add subscription activities
      subscriptions?.forEach((sub) => {
        activityList.push({
          id: sub.id,
          type: 'subscription',
          description: `Subscribed to ${(sub.law_topics as any)?.name || 'topic'}`,
          timestamp: sub.created_at,
        });
      });

      // Add notification activities
      notifications?.forEach((notif) => {
        activityList.push({
          id: notif.id,
          type: 'notification',
          description: `Received notification: ${notif.title}${notif.email_sent ? ' (Email sent)' : ' (Email failed)'}`,
          timestamp: notif.created_at,
        });
      });

      // Add profile update activity
      if (user.updated_at && user.updated_at !== user.created_at) {
        activityList.push({
          id: `profile-${user.id}`,
          type: 'profile_update',
          description: 'Profile updated',
          timestamp: user.updated_at,
        });
      }

      // Sort by timestamp (newest first)
      activityList.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setActivities(activityList.slice(0, 50)); // Limit to 50 most recent
      setUserStats({
        subscriptions: subscriptions?.length || 0,
        notifications: notifications?.length || 0,
        lastActivity: activityList[0]?.timestamp || null,
      });
    } catch (err: any) {
      console.error('Error loading user data:', err);
      showError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'subscription':
        return 'subscriptions';
      case 'notification':
        return 'notifications';
      case 'profile_update':
        return 'edit';
      default:
        return 'circle';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'subscription':
        return 'text-blue-500';
      case 'notification':
        return 'text-green-500';
      case 'profile_update':
        return 'text-purple-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-detail-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-luna-secondary/30 flex items-start justify-between">
          <div className="flex-1">
            <h2 id="user-detail-title" className="text-2xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
              {user.first_name || user.last_name
                ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                : 'User Details'}
            </h2>
            <div className="flex flex-wrap gap-4 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base">badge</span>
                <span>Role: <strong className="text-luna-text-light dark:text-luna-text-dark">{user.role}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base">calendar_today</span>
                <span>Joined: {format(new Date(user.created_at), 'MMM dd, yyyy')}</span>
              </div>
              {user.updated_at && (
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">update</span>
                  <span>Updated: {format(new Date(user.updated_at), 'MMM dd, yyyy HH:mm')}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-luna-secondary/10 text-luna-text-light dark:text-luna-text-dark min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            aria-label="Close user details"
          >
            <span className="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-luna-secondary/5 rounded-lg p-4">
                <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark mb-4">
                  User Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-luna-text-light/50 dark:text-luna-text-dark/50 uppercase">
                      User ID
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-mono text-sm text-luna-text-light dark:text-luna-text-dark">
                        {user.id.substring(0, 8)}...
                      </span>
                      <AdminCopyButton text={user.id} label="User ID" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-luna-text-light/50 dark:text-luna-text-dark/50 uppercase">
                      Role
                    </label>
                    <p className="text-sm text-luna-text-light dark:text-luna-text-dark mt-1 capitalize">
                      {user.role.replace('_', ' ')}
                    </p>
                  </div>
                  {user.updated_at && (
                    <div>
                      <label className="text-xs font-medium text-luna-text-light/50 dark:text-luna-text-dark/50 uppercase">
                        Last Updated
                      </label>
                      <p className="text-sm text-luna-text-light dark:text-luna-text-dark mt-1">
                        {user.updated_at ? format(new Date(user.updated_at), 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-luna-primary border border-luna-secondary/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-blue-500">subscriptions</span>
                    <span className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">
                      Subscriptions
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">
                    {userStats.subscriptions}
                  </p>
                </div>
                <div className="bg-white dark:bg-luna-primary border border-luna-secondary/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-green-500">notifications</span>
                    <span className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">
                      Notifications
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">
                    {userStats.notifications}
                  </p>
                </div>
                <div className="bg-white dark:bg-luna-primary border border-luna-secondary/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-purple-500">schedule</span>
                    <span className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">
                      Last Activity
                    </span>
                  </div>
                  <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                    {userStats.lastActivity
                      ? format(new Date(userStats.lastActivity), 'MMM dd, yyyy')
                      : 'No activity'}
                  </p>
                </div>
              </div>

              {/* Activity Timeline */}
              <div>
                <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark mb-4">
                  Activity Timeline
                </h3>
                {activities.length === 0 ? (
                  <div className="text-center py-8 text-luna-text-light/50 dark:text-luna-text-dark/50">
                    <span className="material-symbols-outlined text-4xl mb-2 block">timeline</span>
                    <p>No activity recorded</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity, index) => (
                      <div
                        key={activity.id}
                        className="flex gap-4 relative"
                      >
                        {/* Timeline line */}
                        {index < activities.length - 1 && (
                          <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-luna-secondary/20" />
                        )}
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-luna-secondary/10 flex items-center justify-center ${getActivityColor(activity.type)}`}>
                          <span className="material-symbols-outlined text-sm">
                            {getActivityIcon(activity.type)}
                          </span>
                        </div>
                        {/* Content */}
                        <div className="flex-1 pb-4">
                          <p className="text-sm text-luna-text-light dark:text-luna-text-dark">
                            {activity.description}
                          </p>
                          <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-1">
                            {format(new Date(activity.timestamp), 'MMM dd, yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

